import { EMPTY_STRING, mergeRefs } from "@regrello/core-utils";
import React, { useCallback, useState } from "react";
import { type FieldPath, type FieldValues, useController } from "react-hook-form";
import { useDebounce } from "react-use";
import { DEBOUNCE_TIMEOUT } from "../../../../../constants/globalConstants";
import {
  RegrelloFormFieldTextMultiline,
  type RegrelloFormFieldTextMultilineProps,
} from "../RegrelloFormFieldTextMultiline";
import { getErrorProp, type RegrelloControlledFormFieldProps, useInvokeOnChangeOnRequiredStateChange } from "./utils";

/**
 * A controlled multiline text field for use with react-hook-form.
 */
function RegrelloControlledFormFieldTextMultilineInternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  controllerProps,
  shouldDebounceOnChangeCallbacks = false,
  onValueChange,
  ...textMultilineFieldProps
}: RegrelloControlledFormFieldProps<
  TFieldValues,
  TName,
  Omit<RegrelloFormFieldTextMultilineProps, "name" | "onChange" | "value">
> & {
  /**
   * Whether to debounce the `onValueChange` and `field.onChange` callbacks. This is a performance
   * optimization to prevent the entire form from having to rerender on every keystroke when this
   * input is used within a large, complex form. It internalizes the value of the field, and only
   * propagates that value up on debounce.
   *
   * @default false
   */
  shouldDebounceOnChangeCallbacks?: boolean;

  /**
   * Capture the change event before the rerender happens. This is useful for setting the
   * "useForm" form values to the correct type before rendering a new RegrelloFormField of a
   * different type. This avoids passing in a incorrect data type to the component.
   */
  onValueChange?: (name: string, newValue: string) => void;
}) {
  const { field, fieldState } = useController(controllerProps);
  const { ref, ...fieldProps } = field;
  const [value, setValue] = useState(field.value ?? EMPTY_STRING);

  const internalOnBlur = fieldProps.onBlur;
  const propsOnBlur = textMultilineFieldProps.onBlur;

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      internalOnBlur();
      propsOnBlur?.(event);
    },
    [internalOnBlur, propsOnBlur],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);

      if (!shouldDebounceOnChangeCallbacks) {
        onValueChange?.(field.name, event.target.value);
        field.onChange(event.target.value);
      }
    },
    [field, onValueChange, shouldDebounceOnChangeCallbacks],
  );

  const debouncedFormUpdateCallback = useCallback(() => {
    if (!shouldDebounceOnChangeCallbacks) {
      return;
    }

    onValueChange?.(field.name, value);
    field.onChange(value);
  }, [field, onValueChange, shouldDebounceOnChangeCallbacks, value]);

  useDebounce(debouncedFormUpdateCallback, DEBOUNCE_TIMEOUT, [value]);
  useInvokeOnChangeOnRequiredStateChange(field, textMultilineFieldProps.isRequiredAsteriskShown ?? false);

  return (
    <RegrelloFormFieldTextMultiline
      {...fieldProps}
      {...textMultilineFieldProps}
      {...getErrorProp(fieldState)}
      inputRef={textMultilineFieldProps.inputRef != null ? mergeRefs(ref, textMultilineFieldProps.inputRef) : ref}
      onBlur={handleBlur}
      onChange={handleChange}
      value={shouldDebounceOnChangeCallbacks ? value : fieldProps.value}
    />
  );
}

export const RegrelloControlledFormFieldTextMultiline = React.memo(
  RegrelloControlledFormFieldTextMultilineInternal,
) as typeof RegrelloControlledFormFieldTextMultilineInternal;
